import type { Theme } from '@mui/material';
import type { GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import {
  DataGridPremium,
  GridRowParams,
  type DataGridPremiumProps,
  type GridApi,
  type GridColDef,
  type GridGroupingColDefOverride,
} from '@mui/x-data-grid-premium';
import { EmptyTableOverlay } from '../../../design/EmptyTableOverlay';
import { hideColumnSeparatorsSx, suppressPinnedColumnSeparatorSx } from './pinned-columns-sx';
import { LoadingOverlay } from './VyTable';

const PINNED_RIGHT_SCROLL_FADE = {
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    right: 'calc(100% + 1px)',
    top: 0,
    height: '100%',
    width: '25px',
    background: (theme: Theme) => `linear-gradient(to right, transparent, ${theme.palette.common.white})`,
    pointerEvents: 'none',
  },
};

type VyTableWaterfallPremiumProps<Item extends GridValidRowModel> = {
  items: Item[];
  columns: GridColDef<Item>[];
  loadingItems?: boolean;
  onRowClick?: (params: GridRowParams<Item>) => void;
  getRowId?: (row: Item) => GridRowId;
  apiRef?: React.MutableRefObject<GridApi>;
  pinnedColumns?: { left?: string[]; right?: string[] };
  pinnedRows?: { top?: Item[]; bottom?: Item[] };
  getRowClassName?: (params: { row: Item }) => string;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  hideFooter?: boolean;
  density?: DataGridPremiumProps['density'];
  scrollbarSize?: DataGridPremiumProps['scrollbarSize'];
  initialColumnVisibility?: Record<string, boolean>;
  treeData?: boolean;
  getTreeDataPath?: (row: Item) => string[];
  groupingColDef?: GridGroupingColDefOverride<Item>;
  defaultGroupingExpansionDepth?: number;
};

export function VyTableWaterfallPremium<Item extends GridValidRowModel>({
  items,
  columns,
  loadingItems,
  onRowClick,
  getRowId,
  apiRef,
  pinnedColumns,
  pinnedRows,
  getRowClassName,
  pageSizeOptions,
  initialPageSize,
  hideFooter,
  density,
  scrollbarSize,
  initialColumnVisibility,
  treeData,
  getTreeDataPath,
  groupingColDef,
  defaultGroupingExpansionDepth,
}: VyTableWaterfallPremiumProps<Item>) {
  return (
    <DataGridPremium
      apiRef={apiRef}
      density={density}
      scrollbarSize={scrollbarSize}
      rows={items}
      columns={columns}
      getRowId={getRowId}
      pagination={!hideFooter}
      hideFooter={hideFooter}
      loading={loadingItems}
      onRowClick={onRowClick}
      pinnedColumns={pinnedColumns}
      pinnedRows={pinnedRows}
      getRowClassName={getRowClassName}
      treeData={treeData}
      getTreeDataPath={getTreeDataPath}
      groupingColDef={groupingColDef}
      defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
      sx={(theme: Theme) => ({
        height: '100%',
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
        ...suppressPinnedColumnSeparatorSx,
        ...hideColumnSeparatorsSx,
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
          lineHeight: 'normal',
        },
        '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor: 'common.white',
          borderRight: `1px solid ${theme.palette.divider}`,
        },
        '& .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: 'common.white',
          borderLeft: `1px solid ${theme.palette.divider}`,
          ...PINNED_RIGHT_SCROLL_FADE,
        },
        '& .MuiDataGrid-pinnedRows': {
          boxShadow: 'none !important',
          backgroundColor: 'common.white',
        },
        '& .MuiDataGrid-pinnedRows--bottom': {
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'common.white',
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: 'common.white',
          },
          marginTop: '-1px',
        },
      })}
      slots={{
        noRowsOverlay: EmptyTableOverlay,
        loadingOverlay: LoadingOverlay,
      }}
      pageSizeOptions={pageSizeOptions ?? [25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: initialPageSize ?? 25,
          },
        },
        columns: {
          columnVisibilityModel: initialColumnVisibility,
        },
      }}
    />
  );
}
